import {
  ActionExample,
  IAgentRuntime,
  Content,
  HandlerCallback,
  Memory,
  Provider,
  State,
  Action,
  logger,
} from '@elizaos/core';
import {
  AppPositionsData,
  GetAppPositionsContent,
  PositionType,
  ZapperConfig,
  ZapperResponse,
} from '../types';
import { fetchWithPolyfill } from '../utils/fetchWithPolyfill';

// Enhanced address extraction using LLM reasoning instead of complex regex
async function extractWalletAddressWithLLM(
  runtime: IAgentRuntime,
  message: Memory,
  state?: State
): Promise<{ address: string | null; confidence: string; reasoning: string }> {
  const extractionPrompt = `
<task>
You are helping extract a wallet address from a conversation about DeFi positions. 
Analyze the provided context and identify any valid wallet address.
</task>

<address_types>
Valid address formats:
1. Ethereum addresses: 42-character strings starting with 0x (e.g., 0x1234567890abcdef1234567890abcdef12345678)
2. ENS names: Domain names ending with .eth (e.g., vitalik.eth, user.eth)  
3. Basename addresses: Domain names ending with .base (e.g., user.base)
</address_types>

<instructions>
1. Read the current message text carefully
2. Look for any of the valid address formats mentioned above
3. If found, extract the EXACT address as written
4. If multiple addresses are present, choose the most recent or contextually relevant one
5. Provide your confidence level and reasoning

DO NOT:
- Modify or truncate addresses
- Accept partial addresses
- Accept invalid formats
- Make up addresses
</instructions>

<context>
Current message: "${message.content.text || ''}"

${state?.recentMessages ? `Recent conversation context: "${state.recentMessages}"` : ''}
</context>

<response_format>
Respond in this exact JSON format:
{
  "address": "extracted_address_or_null",
  "confidence": "high|medium|low|none",
  "reasoning": "Brief explanation of what you found and why"
}
</response_format>

<examples>
Message: "Get my DeFi positions for 0x1234567890abcdef1234567890abcdef12345678"
Response: {"address": "0x1234567890abcdef1234567890abcdef12345678", "confidence": "high", "reasoning": "Found complete Ethereum address explicitly mentioned"}

Message: "Show me vitalik.eth staking positions" 
Response: {"address": "vitalik.eth", "confidence": "high", "reasoning": "Found valid ENS name"}

Message: "What are my positions?"
Response: {"address": null, "confidence": "none", "reasoning": "No wallet address provided in message"}
</examples>
`;

  try {
    const response = await runtime.useModel('TEXT_FAST', {
      messages: [{ role: 'user', content: extractionPrompt }],
      temperature: 0.1, // Low temperature for consistent extraction
      maxTokens: 200,
    });

    const result = JSON.parse(response);
    logger.info('📍 Address extraction completed', {
      address: result.address,
      confidence: result.confidence,
      reasoning: result.reasoning,
    });

    return result;
  } catch (error) {
    logger.error('❌ LLM address extraction failed, falling back to basic pattern matching', {
      error,
    });

    // Simple fallback pattern matching
    const text = message.content.text || '';
    const ethAddress = text.match(/0x[a-fA-F0-9]{40}/)?.[0];
    const ensName = text.match(/\b[\w\-]+\.eth\b/)?.[0];
    const basename = text.match(/\b[\w\-]+\.base\b/)?.[0];

    const address = ethAddress || ensName || basename || null;

    return {
      address,
      confidence: address ? 'medium' : 'none',
      reasoning: address ? 'Found using pattern matching fallback' : 'No address found',
    };
  }
}

export const getAppPositionsAction: Action = {
  name: 'getAppPositions',
  description:
    'Get comprehensive DeFi positions, staking, lending, and liquidity positions for any Ethereum address or ENS name',
  similes: [
    'GET_APP_POSITIONS',
    'GET_DEFI_POSITIONS',
    'GET_PROTOCOL_POSITIONS',
    'GET_STAKING_POSITIONS',
    'GET_LENDING_POSITIONS',
    'GET_LIQUIDITY_POSITIONS',
    'GET_YIELD_POSITIONS',
    'GET_FARM_POSITIONS',
    'SHOW_PORTFOLIO',
    'CHECK_BALANCES',
    'VIEW_INVESTMENTS',
  ],

  validate: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<boolean> => {
    logger.info('🔍 Starting getAppPositions validation');

    // Check API key
    const apiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!apiKey) {
      logger.error('❌ ZAPPER_API_KEY not configured');
      return false;
    }

    // Use enhanced LLM-based address extraction
    const { address, confidence } = await extractWalletAddressWithLLM(runtime, message, state);

    if (!address || confidence === 'none') {
      logger.warn('⚠️ No valid wallet address found for position lookup');
      return false;
    }

    logger.info('✅ Validation passed', {
      address,
      confidence,
      addressType: address.includes('.eth')
        ? 'ENS'
        : address.includes('.base')
          ? 'Basename'
          : 'Ethereum Address',
    });

    return true;
  },

  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    options?: any,
    callback?: HandlerCallback
  ): Promise<boolean> => {
    logger.info('🚀 Starting getAppPositions handler');

    try {
      // Enhanced address extraction
      const {
        address: extractedAddress,
        confidence,
        reasoning,
      } = await extractWalletAddressWithLLM(runtime, message, state);

      if (!extractedAddress) {
        const helpMessage = `I need a wallet address to fetch DeFi positions. Please provide:
        
• An Ethereum address (e.g., 0x1234...)
• An ENS name (e.g., vitalik.eth) 
• A Basename (e.g., user.base)

Try: "Show me DeFi positions for vitalik.eth"`;

        callback?.({
          text: helpMessage,
          content: { error: 'No wallet address provided', reasoning },
        });
        return false;
      }

      logger.info('📍 Using address for lookup', {
        address: extractedAddress,
        confidence,
        reasoning,
      });

      const apiKey = runtime.getSetting('ZAPPER_API_KEY');
      const apiUrl = runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql';

      const config: ZapperConfig = {
        apiKey,
        apiUrl,
      };

      logger.debug('🔧 Zapper configuration prepared', {
        apiUrl,
        hasApiKey: !!apiKey,
      });

      // Parse additional parameters from message if provided
      const content = message.content as Partial<GetAppPositionsContent>;
      logger.debug('🎛️ Parsing additional parameters from message content', {
        hasNetworks: !!content.networks,
        hasAppIds: !!content.appIds,
        hasPositionTypes: !!content.positionTypes,
        contentKeys: Object.keys(content),
      });

      // Parse networks parameter and convert to chainIds
      let networks: string[] = [];
      if (content.networks) {
        if (typeof content.networks === 'string') {
          networks = content.networks.split(',').map((n) => n.trim());
        } else {
          networks = content.networks;
        }
        logger.debug('🌐 Networks parameter parsed', { networks });
      } else {
        logger.debug('🌐 No networks specified, will use defaults');
      }

      // Network name to chain ID mapping
      const NETWORK_TO_CHAIN_ID: { [key: string]: number } = {
        ethereum: 1,
        polygon: 137,
        arbitrum: 42161,
        optimism: 10,
        base: 8453,
        avalanche: 43114,
        bsc: 56,
        fantom: 250,
        gnosis: 100,
        celo: 42220,
        moonbeam: 1284,
        aurora: 1313161554,
        harmony: 1666600000,
        moonriver: 1285,
        metis: 1088,
        cronos: 25,
        evmos: 9001,
        milkomeda: 2001,
        fuse: 122,
        smartbch: 10000,
        syscoin: 57,
        okex: 66,
        heco: 128,
        kava: 2222,
        klaytn: 8217,
        iotex: 4689,
        theta: 361,
        godwoken: 71402,
        kcc: 321,
        wanchain: 888,
        elastos: 20,
        meter: 82,
        rei: 47805,
        astar: 592,
        shiden: 336,
        karura: 686,
        acala: 787,
        parallel: 1024,
        clover: 1023,
        moonrock: 1285,
        shibuya: 81,
        darwinia: 46,
        crab: 44,
        dock: 22,
        edgeware: 2021,
        equilibrium: 1284,
        genshiro: 1285,
        hydradx: 222,
        interlay: 2032,
        kintsugi: 2092,
        nodle: 1285,
        origintrail: 100,
        phala: 1285,
        picasso: 1285,
        robonomics: 1285,
        stafi: 1285,
        turing: 1285,
        zeitgeist: 1285,
      };

      // Convert network names to chain IDs
      const chainIds =
        networks.length > 0
          ? networks.map((network) => {
              const normalized = network.toLowerCase().trim();
              const chainId = NETWORK_TO_CHAIN_ID[normalized] || 1; // Default to Ethereum if unknown
              if (!NETWORK_TO_CHAIN_ID[normalized]) {
                logger.warn('⚠️ Unknown network name, defaulting to Ethereum', {
                  network: normalized,
                  defaultChainId: 1,
                });
              }
              return chainId;
            })
          : [1, 137, 42161, 10, 8453]; // Default to main networks

      logger.info('🔗 Chain IDs configured for query', {
        networksRequested: networks,
        chainIds,
        isDefault: networks.length === 0,
      });

      // Parse appIds parameter
      let appIds: string[] = [];
      if (content.appIds) {
        if (typeof content.appIds === 'string') {
          appIds = content.appIds.split(',').map((id) => id.trim());
        } else {
          appIds = content.appIds;
        }
        logger.debug('🏗️ App IDs filter parsed', { appIds });
      }

      // Parse positionTypes parameter
      let positionTypes: string[] = [];
      if (content.positionTypes) {
        if (typeof content.positionTypes === 'string') {
          positionTypes = [content.positionTypes];
        } else {
          positionTypes = content.positionTypes;
        }
        logger.debug('📊 Position types filter parsed', { positionTypes });
      }

      // Use portfolioV2 with appBalances to get DeFi positions
      const query = `
        query GetAppPositions($addresses: [Address!]!, $chainIds: [Int!]) {
          portfolioV2(addresses: $addresses, chainIds: $chainIds) {
            appBalances {
              byApp(first: 100) {
                edges {
                  node {
                    id
                    appId
                    networkId
                    balanceUSD
                    positionCount
                    app {
                      displayName
                      slug
                      imgUrl
                    }
                    network {
                      name
                      slug
                    }
                    positionBalances(first: 100) {
                      edges {
                        node {
                          ... on AppTokenPositionBalance {
                            type
                            address
                            network
                            balance
                            balanceUSD
                            price
                            symbol
                            decimals
                            key
                            appId
                            groupId
                            groupLabel
                          }
                          ... on ContractPositionBalance {
                            type
                            key
                            address
                            network
                            appId
                            groupId
                            groupLabel
                            balanceUSD
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        addresses: [extractedAddress],
        chainIds: chainIds,
      };

      logger.info('🎯 GraphQL query variables prepared', {
        variables,
        queryLength: query.length,
      });

      logger.info('📡 Making request to Zapper GraphQL API', {
        apiUrl: config.apiUrl,
        addressCount: variables.addresses.length,
        chainIdCount: variables.chainIds.length,
      });

      const response = await fetchWithPolyfill(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-zapper-api-key': config.apiKey,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      logger.debug('📊 API response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ Zapper API request failed', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          apiUrl: config.apiUrl,
          variables,
        });

        if (response.status === 401) {
          logger.error('🔐 Authentication failed - invalid API key');
          throw new Error(
            'Invalid Zapper API key. Please check your ZAPPER_API_KEY configuration.'
          );
        } else if (response.status === 429) {
          logger.error('🚫 Rate limit exceeded');
          throw new Error('Rate limit exceeded. Please try again in a few minutes.');
        } else if (response.status === 404) {
          logger.error('🔍 Address not found', { address: extractedAddress });
          throw new Error(`Address ${extractedAddress} not found or has no DeFi positions.`);
        } else {
          logger.error('💥 Unexpected API error', { status: response.status, errorText });
          throw new Error(
            `Zapper API request failed (${response.status}): ${errorText || 'Unknown error'}`
          );
        }
      }

      logger.debug('✅ API response received successfully');

      logger.debug('🔄 Parsing JSON response from API');
      const data = (await response.json()) as ZapperResponse<{
        portfolioV2: {
          appBalances: {
            byApp: {
              edges: Array<{
                node: {
                  id: string;
                  appId: string;
                  networkId: string;
                  balanceUSD: number;
                  positionCount: number;
                  app: {
                    displayName: string;
                    slug: string;
                    imgUrl: string;
                  };
                  network: {
                    name: string;
                    slug: string;
                  };
                  positionBalances: {
                    edges: Array<{
                      node: {
                        type: string;
                        key?: string;
                        address: string;
                        network: string;
                        appId: string;
                        groupId: string;
                        groupLabel?: string;
                        balanceUSD: number;
                        // AppTokenPositionBalance specific fields
                        balance?: string;
                        price?: number;
                        symbol?: string;
                        decimals?: number;
                      };
                    }>;
                  };
                };
              }>;
            };
          };
        };
      }>;

      logger.debug('📊 Response data structure', {
        hasData: !!data.data,
        hasErrors: !!(data.errors && data.errors.length > 0),
        errorCount: data.errors?.length || 0,
        hasPortfolioV2: !!data.data?.portfolioV2,
        hasAppBalances: !!data.data?.portfolioV2?.appBalances,
        edgeCount: data.data?.portfolioV2?.appBalances?.byApp?.edges?.length || 0,
      });

      if (data.errors && data.errors.length > 0) {
        logger.error('❌ GraphQL errors detected in response', {
          errors: data.errors,
          address: extractedAddress,
          variables,
        });
        const errorMessage = data.errors[0].message;

        if (errorMessage.includes('Invalid address')) {
          logger.error('🚫 Invalid address format detected', {
            address: extractedAddress,
            errorMessage,
          });
          throw new Error(`Invalid wallet address format: ${extractedAddress}`);
        } else if (errorMessage.includes('not found')) {
          logger.error('🔍 Address not found in GraphQL response', {
            address: extractedAddress,
            errorMessage,
          });
          throw new Error(`No DeFi positions found for address ${extractedAddress}`);
        } else {
          logger.error('💥 Unexpected GraphQL error', { errorMessage, address: extractedAddress });
          throw new Error(`Unable to fetch positions: ${errorMessage}`);
        }
      }

      logger.debug('✅ GraphQL response validation passed');

      const portfolioData = data.data?.portfolioV2?.appBalances?.byApp;
      logger.info('📈 Processing portfolio data from response', {
        hasPortfolioData: !!portfolioData,
        hasEdges: !!portfolioData?.edges,
        edgeCount: portfolioData?.edges?.length || 0,
      });

      if (!portfolioData?.edges || portfolioData.edges.length === 0) {
        logger.warn('⚠️ No DeFi positions found in response', {
          address: extractedAddress,
          hasPortfolioData: !!portfolioData,
          hasEdges: !!portfolioData?.edges,
          dataStructure: {
            hasData: !!data.data,
            hasPortfolioV2: !!data.data?.portfolioV2,
            hasAppBalances: !!data.data?.portfolioV2?.appBalances,
            hasByApp: !!data.data?.portfolioV2?.appBalances?.byApp,
          },
        });

        callback?.({
          text: `No DeFi positions found for address ${extractedAddress}. This address might not have any active positions in supported protocols.`,
          content: {
            address: extractedAddress,
            totalValueUSD: 0,
            networks: [],
            positionCount: 0,
            appCount: 0,
            networkCount: 0,
            positions: [],
            message: 'No positions found',
          },
        });
        return true;
      }

      logger.info('✅ Portfolio data found, processing positions', {
        appCount: portfolioData.edges.length,
      });

      // Format app positions from the new structure
      const appPositions: any[] = [];
      logger.debug('🔄 Processing app positions from portfolio data');

      portfolioData.edges.forEach((appEdge, appIndex) => {
        const app = appEdge.node;
        logger.debug(`📱 Processing app ${appIndex + 1}/${portfolioData.edges.length}`, {
          appId: app.appId,
          appName: app.app?.displayName,
          networkName: app.network?.name,
          balanceUSD: app.balanceUSD,
          positionCount: app.positionCount,
          positionBalanceEdges: app.positionBalances?.edges?.length || 0,
        });

        if (!app.positionBalances?.edges) {
          logger.warn('⚠️ App has no position balance edges', {
            appId: app.appId,
            appName: app.app?.displayName,
          });
          return;
        }

        app.positionBalances.edges.forEach((positionEdge, positionIndex) => {
          const position = positionEdge.node;
          logger.debug(
            `📊 Processing position ${positionIndex + 1}/${app.positionBalances.edges.length}`,
            {
              appId: app.appId,
              positionType: position.type,
              groupId: position.groupId,
              groupLabel: position.groupLabel,
              balanceUSD: position.balanceUSD,
              symbol: position.symbol,
            }
          );

          const formattedPosition = {
            id: position.key || `${position.appId}-${position.groupId}`,
            label: position.groupLabel || position.type,
            symbol: position.symbol || 'N/A',
            network: app.network.name,
            address: extractedAddress,
            balance: position.balance || '0',
            balanceUSD: position.balanceUSD,
            appId: app.appId,
            appName: app.app.displayName,
            appDisplayName: app.app.displayName,
            appIconUrl: app.app.imgUrl,
            type: position.type,
            protocol: app.app.slug,
            protocolDisplayName: app.app.displayName,
            protocolIconUrl: app.app.imgUrl,
            suppliedTokens: [], // Not available in this query structure
            borrowedTokens: [], // Not available in this query structure
            rewardTokens: [], // Not available in this query structure
            apy: 0, // Not available in this query structure
            healthFactor: 0, // Not available in this query structure
            price: position.price || 0,
            decimals: position.decimals || 18,
            groupId: position.groupId,
            groupLabel: position.groupLabel,
          };

          logger.debug('✅ Position formatted successfully', {
            positionId: formattedPosition.id,
            symbol: formattedPosition.symbol,
            balanceUSD: formattedPosition.balanceUSD,
            appName: formattedPosition.appName,
            network: formattedPosition.network,
          });

          appPositions.push(formattedPosition);
        });
      });

      logger.info('📊 Calculating portfolio summary statistics', {
        totalPositions: appPositions.length,
      });

      const totalValueUSD = appPositions.reduce((sum, position) => sum + position.balanceUSD, 0);
      const networks_used = Array.from(new Set(appPositions.map((position) => position.network)));
      const uniqueApps = Array.from(new Set(appPositions.map((p) => p.appId).filter(Boolean)));

      logger.debug('💰 Portfolio statistics calculated', {
        totalValueUSD,
        networksUsed: networks_used,
        uniqueAppCount: uniqueApps.length,
        networkCount: networks_used.length,
        positionCount: appPositions.length,
      });

      // Format the response
      const formattedResponse = {
        address: extractedAddress,
        totalValueUSD: totalValueUSD,
        networks: networks_used,
        positionCount: appPositions.length,
        appCount: uniqueApps.length,
        networkCount: networks_used.length,
        positions: appPositions,
      };

      logger.info('✅ Response formatted successfully', {
        address: formattedResponse.address,
        totalValueUSD: formattedResponse.totalValueUSD,
        positionCount: formattedResponse.positionCount,
        appCount: formattedResponse.appCount,
        networkCount: formattedResponse.networkCount,
      });

      if (callback) {
        const responseText = `Found ${formattedResponse.positionCount} DeFi positions across ${formattedResponse.appCount} apps on ${formattedResponse.networkCount} networks with total value of $${formattedResponse.totalValueUSD.toFixed(2)}`;
        logger.info('📤 Sending response to callback', {
          responseText,
          contentSize: JSON.stringify(formattedResponse).length,
        });

        callback({
          text: responseText,
          content: formattedResponse,
        });
      }

      logger.info('✅ getAppPositions handler completed successfully', {
        address: extractedAddress,
        positionsFound: formattedResponse.positionCount,
        totalValue: formattedResponse.totalValueUSD,
      });
      return true;
    } catch (error) {
      const messageText = message.content.text || '';
      const addressMatch = messageText.match(/0x[a-fA-F0-9]{40}|[\w\-]+\.eth|[\w\-]+\.base/);
      const errorAddress = addressMatch ? addressMatch[0] : 'unknown';

      logger.error('❌ Error in getAppPositions handler', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        address: errorAddress,
        messageId: message.id,
      });

      if (callback) {
        let errorMessage = 'Unknown error occurred';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        logger.debug('📤 Sending error response to callback', { errorMessage });

        callback({
          text: `Unable to fetch DeFi positions: ${errorMessage}`,
          content: {
            error: errorMessage,
            address: errorAddress,
          },
        });
      }

      logger.error('❌ getAppPositions handler failed', {
        finalError: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  },

  examples: [
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show me my DeFi positions for 0x1234567890abcdef1234567890abcdef12345678',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 15 DeFi positions across 8 protocols on 3 networks with total value of $12,345.67',
          action: 'GET_APP_POSITIONS',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'What are vitalik.eth staking positions?',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 3 staking positions on Ethereum with total value of $2,567.89',
          action: 'GET_STAKING_POSITIONS',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Check my Uniswap and Aave positions 0xd8da6bf26964af9d7eed9e03e53415d37aa96045',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 7 positions in Uniswap and Aave with total value of $5,432.10',
          action: 'GET_PROTOCOL_POSITIONS',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'How much do I have in DeFi protocols?',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'I need a wallet address to check your DeFi positions. Please provide an Ethereum address (0x...) or ENS name.',
          action: 'GET_APP_POSITIONS',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'user.base portfolio overview',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 12 DeFi positions across 5 protocols on Base network with total value of $8,901.23',
          action: 'GET_APP_POSITIONS',
        },
      },
    ],
  ] as ActionExample[][],
};
